<script setup lang="ts">
  import { computed } from 'vue';
  import { ColoredButton, FieldSearchBar, SelectInput, TabsMenu } from '@/shared/ui';
  import exportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import listIcon from '@/shared/assets/icons/List.svg';
  import type { PermissionMatrixPositionType } from '@/entities/Permission';

  const SEARCH_CRITERION_OPTIONS = [
    { id: 'permissions', name: 'Permissões' },
    { id: 'positions', name: 'Cargos' },
  ];

  const props = defineProps<{
    positionTypeOptions: Array<{ id: string; text: string; icon?: string }>;
    selectedPositionType: PermissionMatrixPositionType;
    selectedCategoryFilter: number | null;
    categoryOptions: Array<{ value: number; label: string }>;
    selectedSearchCriterion: 'permissions' | 'positions';
    initialSearchTerm?: string;
    exportDisabled?: boolean;
    exportLoading?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:selectedPositionType', value: PermissionMatrixPositionType): void;
    (e: 'search', queryString: string): void;
    (e: 'update:selectedCategoryFilter', value: number | null): void;
    (e: 'update:selectedSearchCriterion', value: 'permissions' | 'positions'): void;
    (e: 'export-to-excel'): void;
    (e: 'open-list-permissions-modal'): void;
  }>();

  const searchFields = computed(() => [...SEARCH_CRITERION_OPTIONS]);

  const categoryModelValue = computed<string | number>(() =>
    props.selectedCategoryFilter === null ? 'all' : props.selectedCategoryFilter
  );

  const resolvedCategoryOptions = computed(() => [
    { id: 'all', name: 'Listar todas as permissões' },
    ...props.categoryOptions.map((category) => ({
      id: category.value,
      name: category.label,
    })),
  ]);

  const handleSearch = (queryString: string) => {
    emit('search', queryString);
  };

  const handlePositionTypeChange = (value: string) => {
    emit('update:selectedPositionType', value as PermissionMatrixPositionType);
  };

  const handleSearchCriterionChange = (value: string | number) => {
    emit('update:selectedSearchCriterion', value as 'permissions' | 'positions');
  };

  const handleCategoryFilterChange = (value: string | number) => {
    emit('update:selectedCategoryFilter', value === 'all' ? null : Number(value));
  };

  const handleExportToExcel = () => {
    emit('export-to-excel');
  };

  const openListPermissionsModal = () => {
    emit('open-list-permissions-modal');
  };
</script>

<template>
  <div class="w-full h-full flex flex-col mt-6">
    <div class="flex w-full flex-col items-start gap-6">
      <div class="flex gap-4">
        <ColoredButton
          text="Exportar para Excel"
          :icon="exportExcelIcon"
          icon-alt="Exportar"
          :disabled="exportDisabled"
          :loading="exportLoading"
          @click="handleExportToExcel"
        />
        <ColoredButton
          text="Listar Permissões"
          :icon="listIcon"
          icon-alt="Exportar"
          :disabled="exportDisabled"
          :loading="exportLoading"
          @click="openListPermissionsModal"
        />
      </div>
      <FieldSearchBar
        :search-fields="searchFields"
        :search-field-id="selectedSearchCriterion"
        :initial-search-term="initialSearchTerm"
        :independent-search-field="true"
        :clear-search-on-field-change="false"
        class="w-full"
        @search="handleSearch"
        @update:search-field-id="handleSearchCriterionChange"
      />

      <div class="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <TabsMenu
          :options="positionTypeOptions"
          :selected-option="selectedPositionType"
          @option-selected="handlePositionTypeChange"
        />

        <SelectInput
          :model-value="categoryModelValue"
          :options="resolvedCategoryOptions"
          :auto-select-first="false"
          class="w-full max-w-[224px] lg:ml-auto"
          @update:model-value="handleCategoryFilterChange"
        />
      </div>
    </div>
  </div>
</template>
